import axios from 'axios'

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://jetnova-jul23-dashboard.zakonweb.workers.dev'

const api = axios.create({
  baseURL: `${API_BASE}/api/v1`,
  timeout: 30000,
})

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('agent_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('agent_token')
      localStorage.removeItem('agent_user')
      window.location.href = '/login'
    }
    const apiMessage =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message
    return Promise.reject(new Error(apiMessage || 'Request failed'))
  }
)

// Types
export interface Allocation {
  id: number
  series_id: number
  series_name?: string
  series_code?: string
  departure_date?: string
  return_date?: string
  allocated_qty: number
  sold_qty: number
  available_qty: number
  wholesale_price: number
  currency: string
  advance_received: number
  balance_due: number
  payment_status: string
  cutoff_date?: string
  status: string
}

export interface Passenger {
  id: number
  full_name: string
  passport_no?: string
  passport_expiry?: string
  cnic?: string
  date_of_birth?: string
  gender?: string
  mobile_number?: string
  room_type: string
  booking_status: string
  visa_status: string
  ticket_status: string
  sale_price: number
  amount_paid: number
  balance_due: number
  payment_status: string
}

// API Methods
export const AgentAPI = {
  // Get my allocations (agent-specific)
  getMyAllocations: async (): Promise<Allocation[]> => {
    const response = await api.get('/group-selling/my-allocations')
    return response.data
  },

  // Get allocation details
  getAllocation: async (id: number): Promise<Allocation> => {
    const response = await api.get(`/group-selling/allocations/${id}`)
    return response.data
  },

  // Get passengers for my allocation
  getMyPassengers: async (allocationId: number): Promise<Passenger[]> => {
    const response = await api.get(`/group-selling/allocations/${allocationId}/passengers`)
    return response.data
  },

  // Add passenger
  addPassenger: async (allocationId: number, data: Partial<Passenger>): Promise<Passenger> => {
    const response = await api.post(`/group-selling/allocations/${allocationId}/passengers`, data)
    return response.data
  },

  // Update passenger
  updatePassenger: async (id: number, data: Partial<Passenger>): Promise<Passenger> => {
    const response = await api.put(`/group-selling/passengers/${id}`, data)
    return response.data
  },

  // Delete passenger
  deletePassenger: async (id: number): Promise<void> => {
    await api.delete(`/group-selling/passengers/${id}`)
  },
}

export default AgentAPI
